import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import BottomNav from '../components/ui/BottomNav'
import TopBar from '../components/ui/TopBar'
import Panel from '../components/ui/Panel'
import Chip from '../components/ui/Chip'
import Avatar from '../components/ui/Avatar'
import { buttonClasses } from '../components/ui/Button'
import texts from '../texts'
import { getGames } from '../lib/api'
import type { GameSummary } from '../lib/api'

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}

function chipVariant(n: number): 'mint' | 'red' | 'neutral' {
  return n > 0 ? 'mint' : n < 0 ? 'red' : 'neutral'
}

interface GameRowProps {
  game: GameSummary
  currentUserId: number
  detail: string
}

function GameRow({ game, currentUserId, detail }: GameRowProps) {
  const me = game.players.find((p) => p.userId === currentUserId)
  const score = me?.totalScore ?? 0
  return (
    <Link
      to={`/game/${game.id}`}
      className="flex w-full items-center gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5 text-inherit no-underline"
    >
      <Avatar name={game.name} index={game.id} />
      <div className="min-w-0 flex-1">
        <b className="block">{game.name}</b>
        <span className="text-[0.8em] text-text-2">{detail}</span>
      </div>
      <Chip variant={chipVariant(score)}>{fmt(score)}</Chip>
    </Link>
  )
}

function Games() {
  const [games, setGames] = useState<GameSummary[]>([])
  const [loading, setLoading] = useState(true)
  const currentUserId = parseInt(localStorage.getItem('userId') || '0', 10)

  useEffect(() => {
    const load = () => {
      getGames()
        .then((response) => setGames(response.games))
        .finally(() => setLoading(false))
    }
    load()

    const onVisible = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  if (loading) {
    return (
      <Screen>
        <TopBar title={texts.games.title} />
        <p className="text-text-2">…</p>
        <BottomNav current="game" />
      </Screen>
    )
  }

  const activeGames = games.filter((g) => g.status === 'active')
  const finishedGames = games.filter((g) => g.status !== 'active')

  return (
    <Screen>
      <TopBar title={texts.games.title} />

      <Link to="/game/new" className={buttonClasses({ full: true })}>
        {texts.games.newGame}
      </Link>

      <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
        {texts.games.activeTitle}
      </span>
      {activeGames.length === 0 ? (
        <Panel>
          <p className="text-[0.85em] text-text-2">{texts.games.noActive}</p>
        </Panel>
      ) : (
        <div className="grid gap-2.5">
          {activeGames.map((game) => (
            <GameRow
              key={game.id}
              game={game}
              currentUserId={currentUserId}
              detail={`${game.clubName ?? texts.games.noClub} · ${formatDate(game.createdAt)} · ${texts.games.players(game.players.length)}`}
            />
          ))}
        </div>
      )}

      <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
        {texts.games.finishedTitle}
      </span>
      {finishedGames.length === 0 ? (
        <Panel>
          <p className="text-[0.85em] text-text-2">{texts.games.noFinished}</p>
        </Panel>
      ) : (
        <div className="grid gap-2.5">
          {finishedGames.map((game) => {
            const winner = [...game.players].sort((a, b) => b.totalScore - a.totalScore)[0]
            return (
              <GameRow
                key={game.id}
                game={game}
                currentUserId={currentUserId}
                detail={`${game.clubName ?? texts.games.noClub} · ${formatDate(game.createdAt)} · ${
                  winner ? texts.games.winner(winner.name) : ''
                }`}
              />
            )
          })}
        </div>
      )}

      <BottomNav current="game" />
    </Screen>
  )
}

export default Games
